import { config } from '../../appConfig'
import ChainFusionClient from '../../clients/ChainFusionClient'
import {
  ApproveErc20Params,
  ApproveParams,
  BTCSendOrdinalParams,
  BTCSendParams,
  CustomApproveErc20Params,
  EthCallContractParams,
  MintCkEthParams,
  SendErc20Params,
  SendEthParams,
  SignEthHashParams,
  SignMessageParams,
  SignSolMessageParams,
  SolSendParams,
  SplSendParams,
  UTXOSendParams,
  WalletAction,
  walletActions,
  XTCBurnParams,
} from '../../interfaces/walletActions'
import {
  hasVault,
  isInitialized,
  isPasswordSet,
  isVaultUnlocked,
  keychainManager,
} from '../../keyring'
import { KeychainType } from '../../keyring/keychains/IKeychain'
import { logger } from '../../logger'
import { useConnectionProvider } from '../../state'
import { toHex } from '../../utils/hex'
import { serializeIdentity } from '../../utils/identity/serialize'
import { sendNft } from '../../utils/nftActions/sendNft'
import approveToken from '../../utils/tokenActions/approveToken'
import burnXTC from '../../utils/tokenActions/burnXTC'
import sendToken from '../../utils/tokenActions/sendToken'

export type WalletActionArguments = {
  action: WalletAction
  wallet: string
  payload: any
}

const handleApproveToken = async (params: ApproveParams, currentWallet: string) => {
  const { tokenCanisterId, standard, amount, spender } = params

  return approveToken({
    agent: config.getAgent({ principalId: currentWallet }),
    approveParams: {
      tokenCanisterId,
      standard,
      amount,
      spender,
    },
  })
}

const handleBurnXTC = async (params: XTCBurnParams, currentWallet: string) => {
  return burnXTC(params, config.getAgent({ principalId: currentWallet }))
}

const signMessage = async ({ principalId, message }: SignMessageParams) => {
  const { provider } = useConnectionProvider.getState()

  if (provider === 'plug') {
    throw new Error('Plug connection does not support signMessage')
  }

  const identity = await keychainManager.getIdentity(principalId)
  const signature = await identity.sign(new TextEncoder().encode(message))

  return {
    publicKey: toHex(identity.getPublicKey().toDer()),
    signature: toHex(signature),
  }
}

const signSolMessage = async ({ principalId, message }: SignSolMessageParams) => {
  const solHelperClient = new ChainFusionClient(config.getAgent({ principalId }))

  return solHelperClient.signSolMessage(message)
}

const signEthHash = async ({ principalId, hash }: SignEthHashParams) => {
  const ethHelperClient = new ChainFusionClient(config.getAgent({ principalId }))

  return ethHelperClient.signEthHash(hash)
}

export const processWalletAction = async ({ action, payload, wallet }: WalletActionArguments) => {
  let response

  switch (action) {
    case walletActions.status:
      const isReady = isInitialized()
      const _hasVault = isReady && (await hasVault())
      const unlocked = _hasVault && isVaultUnlocked()
      const hasPassword = _hasVault && (await isPasswordSet())
      response = {
        hasVault: _hasVault,
        unlocked,
        hasPassword,
        isReady,
      }
      break
    case walletActions.unlock:
      try {
        response = await keychainManager.unlock(payload.password)
      } catch (e) {
        response = false
      }

      break
    case walletActions.create:
      const keychain = await keychainManager.addNewKeychain()
      const mnemonic = await keychain.exportKeychain()
      const principals = await keychain.getPrincipals()
      response = { mnemonic, principal: principals[0].toString() }
      break
    case walletActions.import:
      const importedKeychain = await keychainManager.addNewKeychain({
        mnemonic: payload.mnemonic,
        imported: true,
        accountsEnabled: 1,
        type: KeychainType.HDKeychain,
      })
      const newPrincipals = await importedKeychain.getPrincipals()
      response = { principal: newPrincipals[0].toString() }
      break
    case walletActions.lock:
      response = await keychainManager.lock()
      break
    case walletActions.changePassword:
      response = await keychainManager.setPassword(payload.password)
      break
    case walletActions.clearKeychain:
      response = await keychainManager.clearKeychain()
      break
    case walletActions.transferToken:
      const { tokenCanisterId, standard, amount, receiver, memo, tokenId, chain } = payload

      try {
        const transferResponse = await sendToken({
          agent: config.getAgent({ principalId: wallet }),
          sendParams: {
            tokenCanisterId,
            tokenId,
            chain,
            standard,
            amount,
            receiver,
            memo,
          },
        })
        response = transferResponse
      } catch (e: any) {
        logger.error('Failed to send token', {
          originalError: e,
          standard,
          tokenCanisterId,
        })

        response = { error: e.msg || e.toString() }
      }

      break
    case walletActions.sendNFT:
      try {
        const nftTransferResponse = await sendNft({
          agent: config.getAgent({ principalId: wallet }),
          sendParams: payload,
        })
        response = nftTransferResponse
      } catch (e) {
        response = { error: e }
      }

      break
    case walletActions.approveToken:
      response = await handleApproveToken(payload, wallet)
      break
    case walletActions.transferEth:
      const ethHelperClient = new ChainFusionClient(config.getAgent({ principalId: wallet }))

      response = await ethHelperClient.sendEth(payload as SendEthParams)
      break
    case walletActions.sendSol:
      const solHelperClient = new ChainFusionClient(config.getAgent({ principalId: wallet }))

      response = await solHelperClient.sendSol(payload as SolSendParams)
      break
    case walletActions.sendSpl:
      const splHelperClient = new ChainFusionClient(config.getAgent({ principalId: wallet }))

      response = await splHelperClient.sendSpl(payload as SplSendParams)
      break
    case walletActions.transferErc20:
      const chainFusionClient = new ChainFusionClient(config.getAgent({ principalId: wallet }))

      response = await chainFusionClient.sendErc20(payload as SendErc20Params)
      break
    case walletActions.approveErc20:
      const cfClient = new ChainFusionClient(config.getAgent({ principalId: wallet }))
      response = await cfClient.approveErc20(payload as ApproveErc20Params)
      break
    case walletActions.ethCallContract:
      const cClient = new ChainFusionClient(config.getAgent({ principalId: wallet }))
      response = await cClient.ethCallContract(payload as EthCallContractParams)
      break
    case walletActions.customApproveErc20:
      const chainFClient = new ChainFusionClient(config.getAgent({ principalId: wallet }))
      response = await chainFClient.customApproveErc20(payload as CustomApproveErc20Params)
      break
    case walletActions.depositErc20:
      const depositClient = new ChainFusionClient(config.getAgent({ principalId: wallet }))
      response = depositClient.depositErc20(payload as ApproveErc20Params)
      break
    case walletActions.mintCkETH:
      const ethService = new ChainFusionClient(config.getAgent({ principalId: wallet }))
      response = await ethService.mintCkEth(payload as MintCkEthParams)
      break
    case walletActions.transferBTC:
      const cfService = new ChainFusionClient(config.getAgent({ principalId: wallet }))
      response = await cfService.sendBTC(payload as BTCSendParams)
      break
    case walletActions.transferUTXO:
      const utxoService = new ChainFusionClient(config.getAgent({ principalId: wallet }))
      response = await utxoService.sendUTXO(payload as UTXOSendParams)
      break
    case walletActions.transferOrdinal:
      const ordService = new ChainFusionClient(config.getAgent({ principalId: wallet }))
      response = await ordService.sendOrdinal(payload as BTCSendOrdinalParams)
      break
    case walletActions.exportKeychain:
      response = await keychainManager.exportKeychain(payload.principal, payload.password)
      break
    case walletActions.exportPem:
      response = await keychainManager.exportPem(payload.principal, payload.password)
      break
    case walletActions.importPem:
      const { keypair, keyType } = payload

      const keyPairKeychain = await keychainManager.importKeychain({
        type: KeychainType.KeyPairKeychain,
        secretKey: keypair,
        keyType,
      })

      const importedPrincipals = await keyPairKeychain.getPrincipals()
      response = { principal: importedPrincipals[0].toString() }
      break
    case walletActions.burnXTC:
      response = await handleBurnXTC(payload, wallet)
      break
    case walletActions.getPrincipals:
      response = (await keychainManager.getPrincipals()).map((principal) => principal.toString())
      break
    case walletActions.getIdentity:
      const requestedIdentity = await keychainManager.getIdentity(payload.principal)
      response = serializeIdentity(requestedIdentity)
      break
    case walletActions.addAccount:
      const baseKeychain = await keychainManager.getKeychain(payload.principal)
      const newPrincipal = await keychainManager.addNewAccount(baseKeychain)

      response = newPrincipal.toString()
      break
    case walletActions.signMessage:
      response = await signMessage(payload as SignMessageParams)
      break
    case walletActions.signSolMessage:
      response = await signSolMessage(payload as SignSolMessageParams)
      break
    case walletActions.signEthHash:
      response = await signEthHash(payload as SignEthHashParams)
      break
    default:
      response = { error: 'Unsupported' }
      break
  }

  return response
}
